import { Loader2, RotateCcw, ShieldAlert } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { forkDefaultName } from '@/features/schema/fork'
import { representativeSchema } from '@/features/schema/format'
import type { SchemaGroup } from '@/features/schema/types'
import type { Schema } from '@/services/api/schemas'

/** Sentinel option: start over from a blank Person-seeded schema. */
const FROM_SCRATCH = 'scratch'

interface ChangeStartingPointDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The schema being replaced and how many of its versions go with it. */
  current: { schema: Schema; versionCount: number }
  /** Workspace schemas offered as starting points (newest version each). */
  globalGroups: SchemaGroup[]
  caseName?: string
  /** Replace the case schema; `null` source ⇒ start from scratch. */
  onConfirm: (sourceSchemaId: string | null, name: string) => Promise<void>
}

/**
 * Re-pick the case's starting point after it already has a schema. The case
 * built on the wrong base and wants to start over from another workspace schema
 * (or from scratch) — so this is destructive: the current schema and every
 * version of it are deleted, because a case uses exactly one schema (SP-385).
 * Documents already extracted keep their results; only the schema goes.
 */
export function ChangeStartingPointDialog({
  open,
  onOpenChange,
  current,
  globalGroups,
  caseName,
  onConfirm,
}: ChangeStartingPointDialogProps) {
  const [choice, setChoice] = useState<string>(FROM_SCRATCH)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  // Reset the picker each time the dialog opens so a cancelled attempt doesn't
  // leave a stale choice behind.
  useEffect(() => {
    if (open) {
      setChoice(FROM_SCRATCH)
      setName('')
    }
  }, [open])

  const pick = (value: string) => {
    setChoice(value)
    const group = globalGroups.find(g => representativeSchema(g).id === value)
    setName(group ? forkDefaultName(caseName, group.name) : '')
  }

  const submit = async () => {
    const trimmed = name.trim()
    if (!trimmed || busy) return
    setBusy(true)
    try {
      await onConfirm(choice === FROM_SCRATCH ? null : choice, trimmed)
    } finally {
      setBusy(false)
    }
  }

  const { schema, versionCount } = current

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">Change starting point</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Start this case's schema over from a workspace schema, or from scratch.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3">
          <ShieldAlert className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
          <p className="text-xs text-destructive">
            <span className="font-medium">{schema.name}</span> and{' '}
            {versionCount === 1 ? 'its version' : `all ${versionCount} of its versions`} will be
            deleted. Documents already extracted keep their results.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="starting-point" className="text-foreground">
            New starting point
          </Label>
          <Select value={choice} onValueChange={pick}>
            <SelectTrigger id="starting-point" className="max-md:w-full bg-muted border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value={FROM_SCRATCH}>Start from scratch</SelectItem>
              {globalGroups.map(group => {
                const representative = representativeSchema(group)
                return (
                  <SelectItem key={group.name} value={representative.id}>
                    {group.name} v{representative.version}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="starting-point-name" className="text-foreground">
            Name
          </Label>
          <Input
            id="starting-point-name"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') submit()
            }}
            placeholder="Name the case's new schema"
            className="bg-muted border-border"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-border">
            Cancel
          </Button>
          <Button variant="destructive" onClick={submit} disabled={busy || !name.trim()}>
            {busy ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4 mr-1" />
            )}
            Replace schema
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
